import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import axios from "axios";
import {
  FaEnvelope,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaStore,
} from "react-icons/fa";

import { State } from "../interfaces/types";
import { RootState } from "../store";
import { API_URL_STORES } from "../constants";
import authHeader from "../services/auth-header";

interface StoreProfile {
  id: number;
  name: string;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  profilePhotoUrl?: string | null;
  profile_photo_url?: string | null;
  createdAt?: string | null;
  created_at?: string | null;
  updatedAt?: string | null;
  updated_at?: string | null;
}

type AuthUser = NonNullable<State["auth"]["user"]>;

const getAssignedStoreId = (user: AuthUser | null): number | null => {
  const userData = user as any;
  const value =
    userData?.storeId ??
    userData?.store_id ??
    userData?.store?.id ??
    userData?.store?.storeId ??
    userData?.stores?.[0]?.id;

  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const getEmbeddedStore = (user: AuthUser | null): StoreProfile | null => {
  const userData = user as any;
  if (userData?.store?.name) return userData.store;
  if (userData?.stores?.[0]?.name) return userData.stores[0];
  return null;
};

const formatDate = (value?: string | null): string => {
  if (!value) return "No disponible";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No disponible";

  return new Intl.DateTimeFormat("es-CR", {
    year: "numeric",
    month: "long",
    day: "2-digit",
  }).format(date);
};

const AdminHome: React.FC = () => {
  const { user: currentUser } = useSelector((state: State) => state.auth);
  const { isLoggedIn } = useSelector((state: RootState) => state.auth);
  const [store, setStore] = useState<StoreProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [photoFailed, setPhotoFailed] = useState<boolean>(false);

  const assignedStoreId = useMemo(
    () => getAssignedStoreId(currentUser),
    [currentUser]
  );

  useEffect(() => {
    if (!currentUser) return;

    const embeddedStore = getEmbeddedStore(currentUser);
    if (embeddedStore) {
      setStore(embeddedStore);
    }

    if (!assignedStoreId) {
      setError("No hay un local asignado a este usuario.");
      return;
    }

    setLoading(true);
    setError("");
    setPhotoFailed(false);

    axios
      .get<StoreProfile>(`${API_URL_STORES}/${assignedStoreId}`, {
        headers: authHeader(),
      })
      .then((response) => {
        setStore(response.data);
      })
      .catch((err) => {
        console.error("Error loading assigned store:", err);
        setError("No se pudo cargar la informacion del local asignado.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [assignedStoreId, currentUser]);

  if (isLoggedIn && !currentUser) {
    return <div className="store-profile-loading">Cargando usuario...</div>;
  }

  if (!currentUser) {
    return <Navigate to="/admin" replace />;
  }

  const photoUrl = store?.profilePhotoUrl ?? store?.profile_photo_url;
  const createdAt = store?.createdAt ?? store?.created_at;
  const updatedAt = store?.updatedAt ?? store?.updated_at;

  return (
    <section className="store-profile-page">
      <div className="store-profile-shell">
        <div className="store-profile-media" aria-label="Foto del local">
          {photoUrl && !photoFailed ? (
            <img
              src={photoUrl}
              alt={store?.name ? `Foto de ${store.name}` : "Foto del local"}
              className="store-profile-photo"
              onError={() => setPhotoFailed(true)}
            />
          ) : (
            <div className="store-profile-placeholder">
              <FaStore />
            </div>
          )}
        </div>

        <div className="store-profile-content">
          <span className="store-profile-label">Perfil del local</span>
          <h1 className="store-profile-title">
            {loading && !store ? "Cargando local..." : store?.name ?? "Local no disponible"}
          </h1>

          {error && <div className="store-profile-alert">{error}</div>}

          {!loading && store && (
            <div className="store-profile-details">
              <div className="store-profile-row">
                <FaMapMarkerAlt className="store-profile-icon" />
                <div>
                  <strong>Direccion</strong>
                  <span>{store.address || "No registrada"}</span>
                </div>
              </div>

              <div className="store-profile-row">
                <FaPhoneAlt className="store-profile-icon" />
                <div>
                  <strong>Telefono</strong>
                  <span>{store.phone || "No registrado"}</span>
                </div>
              </div>

              <div className="store-profile-row">
                <FaEnvelope className="store-profile-icon" />
                <div>
                  <strong>Correo electronico</strong>
                  <span>{store.email || "No registrado"}</span>
                </div>
              </div>
            </div>
          )}

          {!loading && store && (
            <div className="store-profile-meta">
              <span>Creado: {formatDate(createdAt)}</span>
              <span>Actualizado: {formatDate(updatedAt)}</span>
            </div>
          )}

          {loading && <div className="store-profile-loading">Cargando informacion...</div>}
        </div>
      </div>
    </section>
  );
};

export default AdminHome;
